/**
 * Bearer SSE의 전송과 수명. 이벤트의 도메인 해석은 소비자가 담당한다.
 * 연결·읽기 대기가 끝나지 않는 경우에도 취소하고 다시 연결할 수 있게 한다.
 */
export type ServerEvent = { readonly event: string; readonly data: string };

/** chunk·UTF-8·CRLF 경계와 여러 data 행을 보존한다. 불완전한 마지막 이벤트는 보내지 않는다. */
export class ServerEventParser {
  private line = "";
  private event = "";
  private data: string[] = [];
  private afterCr = false;

  constructor(private readonly receive: (event: ServerEvent) => void) {}

  push(text: string): void {
    for (const character of text) {
      if (this.afterCr && character === "\n") {
        this.afterCr = false;
        continue;
      }
      this.afterCr = character === "\r";
      if (character === "\r" || character === "\n") {
        this.finishLine();
      } else {
        this.line += character;
        // 변경 힌트는 작은 메시지다. 손상된 스트림으로 무한히 메모리를 잡지 않는다.
        if (this.line.length > 65_536) throw new Error("SSE line exceeds limit.");
      }
    }
  }

  private finishLine(): void {
    const line = this.line;
    this.line = "";
    if (line === "") {
      if (this.data.length > 0) {
        this.receive({ event: this.event || "message", data: this.data.join("\n") });
      }
      this.event = "";
      this.data = [];
      return;
    }
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") this.event = value;
    if (field === "data") {
      if (this.data.length >= 64) throw new Error("SSE event exceeds limit.");
      this.data.push(value);
    }
  }
}

type EventStreamOptions = {
  url: string;
  token: string;
  onEvent: (event: ServerEvent) => void;
  onDisconnected: () => void;
  onUnauthorized: () => void;
  fetch?: typeof fetch;
};

export class EventStream {
  private abort: AbortController | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private attempts = 0;

  constructor(private readonly options: EventStreamOptions) {}

  init(): void {
    if (this.running) return;
    this.running = true;
    void this.connect();
  }

  dispose(): void {
    this.running = false;
    this.abort?.abort();
    if (this.retry !== null) clearTimeout(this.retry);
    this.retry = null;
  }

  private async connect(): Promise<void> {
    const abort = new AbortController();
    this.abort = abort;
    const handshake = setTimeout(() => abort.abort(), 10_000);
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    try {
      const response = await (this.options.fetch ?? fetch)(this.options.url, {
        headers: { Authorization: `Bearer ${this.options.token}`, Accept: "text/event-stream" },
        signal: abort.signal,
        cache: "no-store",
        redirect: "error",
      });
      if (!this.running || abort.signal.aborted) return;
      if (response.status === 401 || response.status === 403) {
        this.dispose();
        this.options.onUnauthorized();
        return;
      }
      if (
        !response.ok ||
        !response.headers.get("Content-Type")?.startsWith("text/event-stream") ||
        !response.body
      ) {
        throw new Error("SSE response is unavailable.");
      }
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = new ServerEventParser((event) => {
        if (!this.running || abort.signal.aborted) return;
        if (event.event === "ready") {
          clearTimeout(handshake);
          this.attempts = 0;
        }
        this.options.onEvent(event);
      });
      while (this.running && !abort.signal.aborted) {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => abort.abort(), 45_000);
        const result = await reader.read();
        if (result.done || !this.running || abort.signal.aborted) break;
        parser.push(decoder.decode(result.value, { stream: true }));
      }
    } catch {
      // 스트림은 변경 힌트다. 재연결 중에도 소비자는 GET으로 상태를 확인한다.
    } finally {
      clearTimeout(handshake);
      clearTimeout(watchdog);
      abort.abort();
      try {
        await reader?.cancel();
      } catch {
        /* 이미 끊긴 스트림의 정리 실패는 무시한다. */
      }
      reader?.releaseLock();
      if (this.running && this.abort === abort) {
        this.options.onDisconnected();
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(this.attempts++, 5));
        this.retry = setTimeout(() => void this.connect(), delay);
      }
    }
  }
}
